import { useCallback, useState } from "react"

import type { ChatMessage, EditorTool } from "./editor-types"

const MAX_UNDO_STACK = 30
const MAX_CHAT_MESSAGES = 30

export function useEditorState() {
  const [activeTool, setActiveTool] = useState<EditorTool>("brush")
  const [brushSize, setBrushSize] = useState(20)
  const [maskOpacity, setMaskOpacity] = useState(0.4)
  const [isExporting, setIsExporting] = useState(false)

  // Undo/redo stacks store serialized fabric canvas JSON
  const [undoStack, setUndoStack] = useState<string[]>([])
  const [redoStack, setRedoStack] = useState<string[]>([])

  // Pending restore state — canvas listens to this via useEffect
  const [pendingRestore, setPendingRestore] = useState<string | null>(null)

  // Track unsaved changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)

  // Chat messages
  const [messages, setMessages] = useState<ChatMessage[]>([])

  const pushUndo = useCallback((state: string) => {
    setUndoStack((prev) => [...prev, state].slice(-MAX_UNDO_STACK))
    setRedoStack([])
  }, [])

  const undo = useCallback((currentCanvasState: string) => {
    setUndoStack((prev) => {
      if (prev.length === 0) return prev
      const last = prev[prev.length - 1]
      setRedoStack((r) => [...r, currentCanvasState])
      setPendingRestore(last)
      return prev.slice(0, -1)
    })
  }, [])

  const redo = useCallback((currentCanvasState: string) => {
    setRedoStack((prev) => {
      if (prev.length === 0) return prev
      const last = prev[prev.length - 1]
      setUndoStack((u) => [...u, currentCanvasState])
      setPendingRestore(last)
      return prev.slice(0, -1)
    })
  }, [])

  const clearPendingRestore = useCallback(() => {
    setPendingRestore(null)
  }, [])

  const resetStacks = useCallback(() => {
    setUndoStack([])
    setRedoStack([])
    setPendingRestore(null)
  }, [])

  const addMessage = useCallback((message: Omit<ChatMessage, "id" | "timestamp">) => {
    setMessages((prev) =>
      [
        ...prev,
        {
          ...message,
          id: crypto.randomUUID(),
          timestamp: Date.now(),
        },
      ].slice(-MAX_CHAT_MESSAGES)
    )
  }, [])

  const clearMessages = useCallback(() => {
    setMessages([])
  }, [])

  return {
    // Tool state
    activeTool,
    setActiveTool,
    brushSize,
    setBrushSize,
    maskOpacity,
    setMaskOpacity,
    isExporting,
    setIsExporting,

    // Undo/redo
    undoStack,
    redoStack,
    pushUndo,
    undo,
    redo,
    canUndo: undoStack.length > 0,
    canRedo: redoStack.length > 0,
    pendingRestore,
    clearPendingRestore,
    resetStacks,

    // Unsaved changes
    hasUnsavedChanges,
    setHasUnsavedChanges,

    // Chat
    messages,
    addMessage,
    clearMessages,
  }
}
